/* eslint-disable react-hooks/rules-of-hooks */
import {useQuery} from "react-query";
import {SERVICE_API_URL} from "../utils/constants";
import {requestWithToken, resolveHistoricTimeRange} from "../utils/functions";

import qs from "qs";
import {GetListProvidersResponse, GetListServicesResponse} from "@/types/service.ts";

export function resolveServiceProviders() {
     
    return useQuery<GetListProvidersResponse>(
        "list.providers",
        async () => {
            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/providers`,
                "GET"
            );

            return response?.data?.data?.providers || []
        }, {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            retry: false,
            staleTime: 60 * 60 * 1000,
            suspense: true
        }
    );
}

export function resolveCustomerServices() {
     
    return useQuery<GetListServicesResponse>(
        "customer.services",
        async () => {
            const response = await requestWithToken(
                `${SERVICE_API_URL}/v2/services/categories_n_products`,
                "GET"
            );

            return response?.data
        }, {
            retry: false
        }
    );
}

export function resolveServiceDetail(service_uuid: string | undefined) {
    return useQuery(["customer.services", service_uuid], async () => {
        try {
            const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}`)

            return response?.data?.data?.service
        } catch (exception) {
            console.log(exception)
        }
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 5 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined",
        suspense: true
    })
}

async function resolveBandwidth(url: string) {
    const response = await requestWithToken(url)

    return response?.data?.data?.bandwidth
}

export function resolveServiceAnalyticBandwidthHistoric(service_uuid: string | undefined, query: any, range: string) {
    const current = qs.stringify(query);
    const history = qs.stringify(resolveHistoricTimeRange(query, range))

    // console.log(history)

    return useQuery(["customer.services.usages.bandwidth", service_uuid, query, range], () => Promise.all([
        resolveBandwidth(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/bandwidth?${current}`),
        resolveBandwidth(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/bandwidth?${history}`)
    ]), {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined",
        suspense: true
    })
}

export function resolveServiceAnalyticBandwidth(service_uuid: string | undefined, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.usages.bandwidth", service_uuid, query], async () =>
            resolveBandwidth(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/bandwidth?${query}`)
        , {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            retry: false,
            staleTime: 30 * 60 * 1000,
            enabled: typeof service_uuid !== "undefined"
        })
}

export function resolveServiceAnalyticVolume(service_uuid: string | undefined, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.usages.volume", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/volume?${query}`)

        return response?.data?.data?.volume
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined"
    })
}

export function resolveServiceAnalyticRequest(service_uuid: string | undefined, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.usages.request", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/request?${query}`)

        return response?.data?.data?.request
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined"
    })
}

export function resolveServiceAnalyticViewers(service_uuid: string | undefined, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.viewers", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/viewers?${query}`)

        return response?.data?.data?.viewers?.map((viewer: any, index: number) => ({
            ...viewer,
            id: index
        }))
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined"
    })
}

export function resolveServiceAnalyticStorageUsages(service_uuid: string | undefined, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.storage.usages", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/usages/storage_usages?${query}`)

        return response?.data?.data?.storage_usages
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined"
    })
}

function resolveProfileQuality(name: string) {
    switch (name) {
        case "hdTransmux":
        case "hdTranscoding":
        case "h264HdTranscoding":
        case "h265HdTranscoding":
        case "gpuH264HdTranscoding":
            return 'HD'
        case "uhdTransmux":
        case "uhdTranscoding":
        case "h264UhdTranscoding":
        case "h265UhdTranscoding":
        case "gpuH264UhdTranscoding":
            return 'UHD'
        case "sdTransmux":
        case "sdTranscoding":
        case "h264SdTranscoding":
        case "h265SdTranscoding":
        case "gpuH264SdTranscoding":
            return 'SD'
        default:
            return '-'
    }
}

export function resolveServiceAnalyticUsagesTranscodings(service_uuid: string, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.usages.transcodings", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/durations/transcodings?${query}`)

        const data = response?.data?.data?.transcodings || []

        const months = data?.map((item: any) => item.month);
        const transcodings = data?.map((item: any) => {
            return Object.entries(item)?.map((e: any, index: number) => {
                const [name, value] = e

                return ({
                    id: `${item.month}${index}`,
                    name,
                    quality: resolveProfileQuality(name),
                    value: Number(value),
                    month: item.month
                })
            })?.flat()
        })?.flat();

        return {
            transcodings,
            months
        }
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined",
        suspense: true
    })
}

export function resolveServiceAnalyticUsagesRecordings(service_uuid: string, query: any) {
    query = qs.stringify(query);

    return useQuery(["customer.services.usages.recordings", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/analytic/durations/recordings?${query}`)

        const data = response?.data?.data?.recordings || []

        const months = data?.map((item: any) => item.month);
        const recordings = data

        return {
            recordings,
            months
        }
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined",
        suspense: true
    })
}

export function resolveServiceDetailStreams(service_uuid: string, query: any, paused = false) {
    query = qs.stringify(query);

    return useQuery(["customer.services.streams", service_uuid, query], async () => {
        const response = await requestWithToken(`${SERVICE_API_URL}/v2/services/${service_uuid}/streams?${query}`)

        return response?.data?.data?.streams || []
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        staleTime: 30 * 60 * 1000,
        enabled: typeof service_uuid !== "undefined",
        suspense: true,
        refetchInterval: !paused && 15000 || false
    })
}

export async function resolvePrivateManifest(payload: any) {
    const query = qs.stringify(payload);

    const response = await fetch(`https://live.aes.vnetwork.dev/drm/getfile?${query}`)
    const data = await response.json()

    return data?.url
}